import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { PageBody, PageHead } from '../components/Page';
import { Badge } from '../components/Pill';
import { StatusBanner } from '../components/StatusBanner';
import { Icon } from '../components/Brand';
import { intlLocale } from '../i18n/formats';
import { useLocale } from '../i18n/LocaleProvider';
import { getDisplayName } from '../lib/auth';
import {
  MAX_FIELD_LENGTH,
  MAX_SIGNATURE_NAME_LENGTH,
  MAX_URL_LENGTH,
  renderSignatureHtml,
  renderSignatureText,
  validateSignatureInput,
  validateSignatureName,
  type SignatureField,
  type SignatureFieldError,
} from '../lib/email-signature/signature';
import { lintSignatureHtml } from '../lib/email-signature/signature-lint';
import {
  appleMailSignatureBody,
  signatureFileBase,
  signatureHtmlDocument,
  signatureOutlookText,
  signaturePreviewDocument,
  signatureRtf,
} from '../lib/email-signature/signature-exports';
import {
  duplicateSignatureForm,
  formFromSaved,
  isSignatureConflict,
  isSignatureGone,
  newSignatureForm,
  signatureFormsDiffer,
  signatureServerErrors,
  suggestSignatureName,
  uniqueSignatureName,
  type SavedSignature,
  type SignatureForm,
  type SignatureServerErrors,
} from '../lib/email-signature/signature-library';
import {
  copyPlainText,
  copyRichText,
  downloadTextFile,
} from '../lib/email-signature/signature-clipboard';
import {
  INSTALL_CLIENT_PARAM,
  SIGNATURE_CLIENTS,
  detectSignatureDevice,
  parseSignatureClient,
  signatureInstallLink,
  signatureInstallMailto,
  type SignatureClient,
} from '../lib/email-signature/signature-install';
import {
  createEmailSignature,
  deleteEmailSignature,
  listEmailSignatures,
  updateEmailSignature,
} from '../lib/ops-email-signatures-api';

/** Catalogue key per validation error — literal, so the parity spec's key scan sees every one. */
const ERROR_KEYS: Record<SignatureFieldError, string> = {
  required: 'signature.errors.required',
  tooLong: 'signature.errors.tooLong',
  invalidEmail: 'signature.errors.invalidEmail',
  invalidPhone: 'signature.errors.invalidPhone',
  invalidUrl: 'signature.errors.invalidUrl',
};

const CLIENT_LABEL_KEYS: Record<SignatureClient, string> = {
  appleMac: 'signature.install.appleMac.tab',
  appleIos: 'signature.install.appleIos.tab',
  thunderbird: 'signature.install.thunderbird.tab',
  outlookClassic: 'signature.install.outlookClassic.tab',
  outlookNew: 'signature.install.outlookNew.tab',
  mobileApps: 'signature.install.mobileApps.tab',
};

/** Where macOS Mail keeps signatures. `V10` is Sonoma and later; older releases count lower. */
const APPLE_MAIL_PATH = `~/Library/Mail/V10/MailData/Signatures`;
const APPLE_MAIL_ICLOUD_PATH = `~/Library/Mobile Documents/com~apple~mail/Data/V10/MailData/Signatures`;
const OUTLOOK_PATH = `%APPDATA%\\Microsoft\\Signatures`;
const THUNDERBIRD_SEPARATOR_PREF = `mail.identity.default.suppress_signature_separator`;

/** React Query key of the saved-signature list. */
const SIGNATURES_QUERY_KEY = ['email-signatures'] as const;

type CopyTarget = 'rich' | 'text' | 'html' | 'apple' | 'link';

/** What the operator asked for while the form held unsaved edits — held until they confirm. */
type PendingSwitch =
  | { readonly kind: 'open'; readonly signature: SavedSignature }
  | { readonly kind: 'new' }
  | { readonly kind: 'discard' };

/** Why the last save did not land, beyond per-field errors. */
type SaveProblem = 'conflict' | 'gone' | 'failed';

/**
 * The support team's email-signature generator, and the library of the signatures it saved.
 *
 * The signature is written once and then lives in each operator's own mail client, so the page's
 * job is to get ONE linted fragment into four clients without any of them rewriting it. Most take
 * a rich-text paste; Thunderbird and classic Outlook read a file instead, and Apple Mail on the
 * Mac keeps a file path as the fallback for when a paste attaches the logo.
 *
 * Signatures are SAVED on the server (`internal-admin/email-signatures`), several per person, so a
 * signature is reopened and edited rather than retyped — and is there on the phone, where every
 * mobile signature is pasted. The selected one is in the URL (`?id=`), and the desk sends the phone
 * a link to it (`&install=` opens the guide). On a phone or tablet the page leads with the install
 * guide for that device. Everything that decides what the signature looks like is in `lib/email-signature/`;
 * see `docs/architecture/email-signatures.md`.
 */
export function SignaturePage() {
  const { t, tNode, locale } = useLocale();
  const displayName = getDisplayName();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();

  const signatures = useQuery({
    queryKey: SIGNATURES_QUERY_KEY,
    queryFn: listEmailSignatures,
  });
  const existingNames = useMemo(
    () => (signatures.data ?? []).map((signature) => signature.name),
    [signatures.data],
  );

  /** The saved version being edited; null for a signature that exists only on this screen. */
  const [baseline, setBaseline] = useState<SavedSignature | null>(null);
  /** Where an unsaved signature started — what "unsaved changes" of a new one is judged against. */
  const [origin, setOrigin] = useState<SignatureForm>(() =>
    newSignatureForm(displayName, locale, [], t('signature.library.newName')),
  );
  const [form, setForm] = useState<SignatureForm>(origin);
  const [touched, setTouched] = useState<ReadonlySet<SignatureField | 'name'>>(
    new Set(),
  );
  const [serverErrors, setServerErrors] =
    useState<SignatureServerErrors | null>(null);
  const [saveProblem, setSaveProblem] = useState<SaveProblem | null>(null);
  const [notice, setNotice] = useState<'saved' | 'deleted' | null>(null);
  const [pending, setPending] = useState<PendingSwitch | null>(null);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [device] = useState(() =>
    detectSignatureDevice(navigator.userAgent, navigator.maxTouchPoints),
  );
  // The guide a link from the desk names, else the one for this device. Read once: the URL is
  // rewritten to `?id=` alone as soon as a signature opens.
  const [client, setClient] = useState<SignatureClient>(
    () =>
      parseSignatureClient(searchParams.get(INSTALL_CLIENT_PARAM)) ??
      device.client,
  );
  const [viewport, setViewport] = useState<'desktop' | 'mobile'>('desktop');
  const [blockImages, setBlockImages] = useState(false);
  const [darkMode, setDarkMode] = useState(false);
  const [copied, setCopied] = useState<{
    readonly target: CopyTarget;
    readonly ok: boolean;
  } | null>(null);

  const reference = baseline ? formFromSaved(baseline) : origin;
  const hasUnsavedEdits = signatureFormsDiffer(reference, form);

  // Open what the URL names, once, when the list first arrives. Later navigation goes through
  // `load`/`startNew`, which keep the URL in step themselves.
  const opened = useRef(false);
  useEffect(() => {
    if (opened.current || !signatures.data) return;
    opened.current = true;
    const id = searchParams.get('id');
    const match = id
      ? signatures.data.find((signature) => signature.id === id)
      : undefined;
    if (match) {
      load(match);
      return;
    }
    if (id) setSearchParams({}, { replace: true });
    // The prefilled name was suggested before the list was known; make it unique now.
    const unique = uniqueSignatureName(origin.name, existingNames);
    setOrigin((current) => ({ ...current, name: unique }));
    setForm((current) =>
      current.name === origin.name ? { ...current, name: unique } : current,
    );
    // Deliberately keyed on the first list only: re-running on a refetch would reopen the URL's
    // signature over whatever the operator is editing now.
  }, [signatures.data]);

  // A tab closed over unsaved edits loses them without a word otherwise.
  useEffect(() => {
    if (!hasUnsavedEdits) return undefined;
    function warn(event: BeforeUnloadEvent) {
      event.preventDefault();
    }
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [hasUnsavedEdits]);

  useEffect(() => {
    if (!copied) return undefined;
    const timer = window.setTimeout(() => setCopied(null), 2500);
    return () => window.clearTimeout(timer);
  }, [copied]);

  useEffect(() => {
    if (!notice) return undefined;
    const timer = window.setTimeout(() => setNotice(null), 3000);
    return () => window.clearTimeout(timer);
  }, [notice]);

  const errors = useMemo(() => validateSignatureInput(form), [form]);
  const nameError = validateSignatureName(form.name);
  const html = useMemo(() => renderSignatureHtml(form), [form]);
  const violations = useMemo(() => lintSignatureHtml(html), [html]);
  const isValid = Object.keys(errors).length === 0;
  const canExport = isValid && violations.length === 0;
  const canSave =
    canExport && !nameError && (baseline === null || hasUnsavedEdits);
  const fileBase = signatureFileBase(form);

  function resetFeedback() {
    setTouched(new Set());
    setServerErrors(null);
    setSaveProblem(null);
  }

  function load(signature: SavedSignature) {
    setBaseline(signature);
    setForm(formFromSaved(signature));
    resetFeedback();
    setSearchParams({ id: signature.id });
  }

  function startNew() {
    const fresh = newSignatureForm(
      displayName,
      locale,
      existingNames,
      t('signature.library.newName'),
    );
    setBaseline(null);
    setOrigin(fresh);
    setForm(fresh);
    resetFeedback();
    setSearchParams({});
  }

  function perform(action: PendingSwitch) {
    if (action.kind === 'open') load(action.signature);
    else if (action.kind === 'new') startNew();
    else if (baseline) load(baseline);
    else startNew();
  }

  /** Runs a switch now, or holds it behind the discard dialog while there are unsaved edits. */
  function requestSwitch(action: PendingSwitch) {
    if (hasUnsavedEdits) setPending(action);
    else perform(action);
  }

  /** Keeps the form's content as a NEW, unsaved signature under a unique copy name. */
  function duplicate() {
    const copy = duplicateSignatureForm(
      form,
      existingNames,
      t('signature.library.copySuffix'),
    );
    setBaseline(null);
    setOrigin(copy);
    setForm(copy);
    setServerErrors(null);
    setSaveProblem(null);
    setSearchParams({});
  }

  /** After a conflict or a deletion elsewhere: keep what is on screen, as a new signature. */
  function keepAsNew() {
    const kept = {
      ...form,
      name: uniqueSignatureName(form.name, existingNames),
    };
    setBaseline(null);
    // Judged against an empty origin, so the kept content counts as unsaved until it is saved.
    setOrigin({ ...kept, name: '' });
    setForm(kept);
    setSaveProblem(null);
    setSearchParams({});
  }

  async function reloadCurrent() {
    const id = baseline?.id;
    const fresh = await signatures.refetch();
    const match = fresh.data?.find((signature) => signature.id === id);
    if (match) load(match);
    else startNew();
  }

  const save = useMutation({
    mutationFn: () =>
      baseline
        ? updateEmailSignature(baseline.id, form, baseline.updatedAt)
        : createEmailSignature(form),
    onSuccess: (result) => {
      // The FORM is left as it is — keystrokes typed while the request was in flight must survive.
      // The stored version is normalised; the dirty check compares normalised values, so the form
      // reads as saved even where it still shows a trailing space.
      setBaseline(result.signature);
      setServerErrors(null);
      setSaveProblem(null);
      setNotice('saved');
      setSearchParams({ id: result.signature.id }, { replace: true });
      void queryClient.invalidateQueries({ queryKey: SIGNATURES_QUERY_KEY });
    },
    onError: (error) => {
      const fieldErrors = signatureServerErrors(error);
      if (fieldErrors) {
        setServerErrors(fieldErrors);
        setSaveProblem(null);
        return;
      }
      setSaveProblem(
        isSignatureConflict(error)
          ? 'conflict'
          : isSignatureGone(error)
            ? 'gone'
            : 'failed',
      );
    },
  });

  const remove = useMutation({
    mutationFn: async (id: string) => {
      try {
        await deleteEmailSignature(id);
      } catch (error) {
        // Already deleted by someone else: the outcome the operator asked for.
        if (!isSignatureGone(error)) throw error;
      }
    },
    onSuccess: () => {
      setConfirmingDelete(false);
      setNotice('deleted');
      startNew();
      void queryClient.invalidateQueries({ queryKey: SIGNATURES_QUERY_KEY });
    },
  });

  function update<K extends keyof SignatureForm>(
    key: K,
    value: SignatureForm[K],
  ) {
    setForm((current) => {
      const next = { ...current, [key]: value };
      // Never when the NAME is what changed: `touched` is still the pre-keystroke set in here.
      return key !== 'name' && followsSuggestion(current)
        ? {
            ...next,
            name: suggestSignatureName(
              next.fullName,
              next.locale,
              existingNames,
              t('signature.library.newName'),
            ),
          }
        : next;
    });
    // A server verdict is about the text it was given; an edit makes it stale.
    if (serverErrors) setServerErrors(null);
  }

  /**
   * Whether a NEW signature's name is still the suggested one — then it follows the full name and
   * language as they are typed ("Erik Arens (DE)" becomes "(EN)" when the language flips). A name
   * the operator has typed into, or any saved signature's name, is theirs and never changes.
   */
  function followsSuggestion(current: SignatureForm): boolean {
    return (
      baseline === null &&
      !touched.has('name') &&
      current.name ===
        suggestSignatureName(
          current.fullName,
          current.locale,
          existingNames,
          t('signature.library.newName'),
        )
    );
  }

  function touch(field: SignatureField | 'name') {
    setTouched((current) => new Set(current).add(field));
  }

  function fieldError(field: SignatureField): string | undefined {
    const error = errors[field] ?? serverErrors?.[field];
    // A required field the operator has not reached yet is not an error to shout about.
    if (!error || (error === 'required' && !touched.has(field)))
      return undefined;
    // The photo URL has a bound of its own — quoting the free-text one would name a limit the
    // validator does not enforce.
    return t(ERROR_KEYS[error], {
      max: field === 'photoUrl' ? MAX_URL_LENGTH : MAX_FIELD_LENGTH,
    });
  }

  function nameFieldError(): string | undefined {
    const error = nameError ?? serverErrors?.name;
    if (!error || (error === 'required' && !touched.has('name')))
      return undefined;
    return t(ERROR_KEYS[error], { max: MAX_SIGNATURE_NAME_LENGTH });
  }

  async function copy(target: CopyTarget) {
    if (!canExport) return;
    let ok: boolean;
    if (target === 'rich')
      ok = await copyRichText(html, renderSignatureText(form));
    else if (target === 'text')
      ok = await copyPlainText(renderSignatureText(form));
    else if (target === 'apple')
      ok = await copyPlainText(appleMailSignatureBody(form));
    else ok = await copyPlainText(html);
    setCopied({ target, ok });
  }

  function download(extension: 'html' | 'htm' | 'rtf' | 'txt') {
    if (!canExport) return;
    if (extension === 'html' || extension === 'htm') {
      downloadTextFile(
        `${fileBase}.${extension}`,
        signatureHtmlDocument(form),
        'text/html;charset=utf-8',
      );
    } else if (extension === 'rtf') {
      downloadTextFile(
        `${fileBase}.rtf`,
        signatureRtf(form),
        'application/rtf',
      );
    } else {
      downloadTextFile(
        `${fileBase}.txt`,
        signatureOutlookText(form),
        'text/plain;charset=utf-8',
      );
    }
  }

  function copyButton(target: CopyTarget, labelKey: string, primary = false) {
    const state = copied?.target === target ? copied : null;
    return (
      <button
        type="button"
        onClick={() => void copy(target)}
        disabled={!canExport}
        className={clsx(
          primary ? 'btn-primary' : 'btn-neutral',
          'btn-sm gap-2',
        )}
      >
        <Icon name="document" className="h-4 w-4" />
        {state
          ? t(
              state.ok
                ? 'signature.actions.copied'
                : 'signature.actions.copyFailed',
            )
          : t(labelKey)}
      </button>
    );
  }

  function downloadButton(extension: 'html' | 'htm' | 'rtf' | 'txt') {
    return (
      <button
        type="button"
        onClick={() => download(extension)}
        disabled={!canExport}
        className="btn-neutral btn-sm gap-2"
      >
        <Icon name="download" className="h-4 w-4" />
        {t('signature.actions.download', { file: `${fileBase}.${extension}` })}
      </button>
    );
  }

  const code = (value: string) => (
    <code className="font-mono text-xs bg-gray-100 rounded px-1 py-0.5 break-all">
      {value}
    </code>
  );
  const strong = (value: string) => (
    <strong className="font-semibold">{value}</strong>
  );

  const installLink = baseline
    ? signatureInstallLink(window.location.href, baseline.id, client)
    : null;
  const canShare = typeof navigator.share === 'function';

  async function copyLink() {
    if (!installLink) return;
    setCopied({ target: 'link', ok: await copyPlainText(installLink) });
  }

  function shareLink() {
    if (!installLink) return;
    // A dismissed share sheet rejects; there is nothing to report.
    navigator
      .share({
        title: t('signature.install.phone.mailSubject'),
        url: installLink,
      })
      .catch(() => undefined);
  }

  /**
   * How the phone gets the signature from the desk: a link to the SAVED version, on this guide.
   * The phone opens what the server holds, so an unsaved signature has no link, and unsaved edits
   * would not arrive with it.
   */
  const phoneLink = (
    <div className="w-full space-y-3 rounded-lg bg-gray-50 px-4 py-3">
      <div>
        <p className="text-sm font-medium text-gray-900">
          {t('signature.install.phone.title')}
        </p>
        <p className="mt-1 text-sm text-gray-600">
          {t('signature.install.phone.lead')}
        </p>
      </div>
      {installLink ? (
        <>
          {hasUnsavedEdits && (
            <StatusBanner tone="warning">
              {t('signature.install.phone.unsaved')}
            </StatusBanner>
          )}
          <div className="flex flex-wrap gap-2">
            <a
              href={signatureInstallMailto(
                form.email,
                t('signature.install.phone.mailSubject'),
                `${t('signature.install.phone.mailBody', { name: baseline?.name ?? '' })}\n\n${installLink}`,
              )}
              className="btn-primary btn-sm gap-2"
            >
              <Icon name="mail" className="h-4 w-4" />
              {t('signature.install.phone.mail', { email: form.email })}
            </a>
            <button
              type="button"
              onClick={() => void copyLink()}
              className="btn-neutral btn-sm gap-2"
            >
              <Icon name="document" className="h-4 w-4" />
              {copied?.target === 'link'
                ? t(
                    copied.ok
                      ? 'signature.actions.copied'
                      : 'signature.actions.copyFailed',
                  )
                : t('signature.install.phone.copyLink')}
            </button>
            {canShare && (
              <button
                type="button"
                onClick={shareLink}
                className="btn-neutral btn-sm"
              >
                {t('signature.install.phone.share')}
              </button>
            )}
          </div>
        </>
      ) : (
        <StatusBanner tone="info">
          {t('signature.install.phone.saveFirst')}
        </StatusBanner>
      )}
    </div>
  );

  const librarySection = (
    <section
      className="card space-y-3"
      aria-labelledby="signature-library-title"
    >
      <div className="flex items-center justify-between gap-3">
        <h2
          id="signature-library-title"
          className="text-base font-semibold text-gray-900"
        >
          {t('signature.library.title')}
        </h2>
        <button
          type="button"
          onClick={() => requestSwitch({ kind: 'new' })}
          className="btn-secondary btn-sm"
        >
          {t('signature.library.new')}
        </button>
      </div>

      {signatures.isError ? (
        <StatusBanner tone="error">
          {t('signature.library.loadFailed')}
        </StatusBanner>
      ) : !signatures.data ? (
        <StatusBanner tone="loading">{t('common.loading')}</StatusBanner>
      ) : signatures.data.length === 0 ? (
        <p className="text-sm text-gray-600">{t('signature.library.empty')}</p>
      ) : (
        <ul className="-mx-2 space-y-1">
          {signatures.data.map((signature) => {
            const active = baseline?.id === signature.id;
            return (
              <li key={signature.id}>
                <button
                  type="button"
                  onClick={() =>
                    active
                      ? undefined
                      : requestSwitch({ kind: 'open', signature })
                  }
                  aria-current={active ? 'true' : undefined}
                  className={clsx(
                    'w-full text-left rounded-lg px-2 py-2 transition-colors',
                    active
                      ? 'bg-primary-50 ring-1 ring-primary-200'
                      : 'hover:bg-gray-50',
                  )}
                >
                  <span className="flex items-center justify-between gap-2">
                    <span
                      className={clsx(
                        'text-sm font-medium truncate',
                        active ? 'text-primary-800' : 'text-gray-900',
                      )}
                    >
                      {signature.name}
                    </span>
                    <Badge tone="gray">{signature.locale.toUpperCase()}</Badge>
                  </span>
                  <span className="block text-xs text-gray-500 truncate">
                    {t('signature.library.updated', {
                      who: signature.updatedBy,
                      when: new Date(signature.updatedAt).toLocaleString(
                        intlLocale(locale),
                        {
                          dateStyle: 'medium',
                          timeStyle: 'short',
                        },
                      ),
                    })}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );

  const formSection = (
    <section className="card space-y-4" aria-labelledby="signature-form-title">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2
          id="signature-form-title"
          className="text-base font-semibold text-gray-900"
        >
          {t(baseline ? 'signature.form.titleEdit' : 'signature.form.titleNew')}
        </h2>
        {baseline === null ? (
          <Badge tone="amber">{t('signature.library.notSaved')}</Badge>
        ) : hasUnsavedEdits ? (
          <Badge tone="amber">{t('signature.library.unsavedChanges')}</Badge>
        ) : (
          <Badge tone="teal">{t('signature.library.saved')}</Badge>
        )}
      </div>

      {saveProblem === 'conflict' && (
        <StatusBanner tone="warning">
          <p>{t('signature.library.conflict')}</p>
          <div className="mt-2 flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => void reloadCurrent()}
              className="btn-neutral btn-sm"
            >
              {t('signature.library.loadCurrent')}
            </button>
            <button
              type="button"
              onClick={keepAsNew}
              className="btn-neutral btn-sm"
            >
              {t('signature.library.keepAsNew')}
            </button>
          </div>
        </StatusBanner>
      )}
      {saveProblem === 'gone' && (
        <StatusBanner tone="warning">
          <p>{t('signature.library.gone')}</p>
          <div className="mt-2">
            <button
              type="button"
              onClick={keepAsNew}
              className="btn-neutral btn-sm"
            >
              {t('signature.library.keepAsNew')}
            </button>
          </div>
        </StatusBanner>
      )}
      {saveProblem === 'failed' && (
        <StatusBanner tone="error">
          {t('signature.library.saveFailed')}
        </StatusBanner>
      )}
      {remove.isError && (
        <StatusBanner tone="error">
          {t('signature.library.deleteFailed')}
        </StatusBanner>
      )}

      <TextField
        id="signature-name"
        label={t('signature.form.name')}
        value={form.name}
        onChange={(value) => {
          touch('name');
          update('name', value);
        }}
        onBlur={() => touch('name')}
        error={nameFieldError()}
        hint={t('signature.form.nameHint')}
      />
      <TextField
        id="signature-full-name"
        label={t('signature.form.fullName')}
        value={form.fullName}
        onChange={(value) => update('fullName', value)}
        onBlur={() => touch('fullName')}
        error={fieldError('fullName')}
        autoComplete="name"
      />
      <TextField
        id="signature-job-title"
        label={t('signature.form.jobTitle')}
        value={form.jobTitle}
        onChange={(value) => update('jobTitle', value)}
        onBlur={() => touch('jobTitle')}
        error={fieldError('jobTitle')}
        placeholder={t('signature.form.jobTitlePlaceholder')}
        autoComplete="organization-title"
      />
      <TextField
        id="signature-email"
        type="email"
        label={t('signature.form.email')}
        value={form.email}
        onChange={(value) => update('email', value)}
        onBlur={() => touch('email')}
        error={fieldError('email')}
        placeholder={t('signature.form.emailPlaceholder')}
        autoComplete="email"
      />
      <TextField
        id="signature-phone"
        type="tel"
        label={t('signature.form.phone')}
        value={form.phone}
        onChange={(value) => update('phone', value)}
        onBlur={() => touch('phone')}
        error={fieldError('phone')}
        hint={t('signature.form.phoneHint')}
        autoComplete="tel"
      />
      <TextField
        id="signature-mobile"
        type="tel"
        label={t('signature.form.mobile')}
        value={form.mobile}
        onChange={(value) => update('mobile', value)}
        onBlur={() => touch('mobile')}
        error={fieldError('mobile')}
        autoComplete="tel"
      />
      <TextField
        id="signature-photo-url"
        type="url"
        label={t('signature.form.photoUrl')}
        value={form.photoUrl}
        onChange={(value) => update('photoUrl', value)}
        onBlur={() => touch('photoUrl')}
        error={fieldError('photoUrl')}
        hint={t('signature.form.photoUrlHint')}
        placeholder="https://"
      />

      <fieldset>
        <legend className="field-label">{t('signature.form.language')}</legend>
        <div className="flex gap-4">
          {(['de', 'en'] as const).map((value) => (
            <label
              key={value}
              className="inline-flex items-center gap-2 text-sm text-gray-700"
            >
              <input
                type="radio"
                name="signature-language"
                checked={form.locale === value}
                onChange={() => update('locale', value)}
                className="w-4 h-4 border-gray-300"
              />
              {t(
                value === 'de'
                  ? 'signature.form.languageDe'
                  : 'signature.form.languageEn',
              )}
            </label>
          ))}
        </div>
        <p className="field-hint">{t('signature.form.languageHint')}</p>
      </fieldset>

      <div className="space-y-2">
        <CheckboxField
          label={t('signature.form.showLogo')}
          hint={t('signature.form.showLogoHint')}
          checked={form.showLogo}
          onChange={(value) => update('showLogo', value)}
        />
        <CheckboxField
          label={t('signature.form.showLegal')}
          hint={t('signature.form.showLegalHint')}
          checked={form.showLegal}
          onChange={(value) => update('showLegal', value)}
        />
        <p className="field-hint">{t('signature.form.disclaimerNote')}</p>
      </div>

      <div className="flex flex-wrap gap-2 border-t border-gray-100 pt-4">
        <button
          type="button"
          onClick={() => save.mutate()}
          disabled={!canSave || save.isPending}
          className="btn-primary btn-sm"
        >
          {t(
            save.isPending
              ? 'signature.library.saving'
              : 'signature.library.save',
          )}
        </button>
        <button
          type="button"
          onClick={duplicate}
          className="btn-neutral btn-sm"
        >
          {t('signature.library.duplicate')}
        </button>
        <button
          type="button"
          onClick={() => requestSwitch({ kind: 'discard' })}
          disabled={!hasUnsavedEdits}
          className="btn-neutral btn-sm"
        >
          {t('signature.form.reset')}
        </button>
        {baseline && (
          <button
            type="button"
            onClick={() => setConfirmingDelete(true)}
            className="btn-neutral btn-sm text-red-700 hover:text-red-800"
          >
            {t('signature.library.delete')}
          </button>
        )}
      </div>
    </section>
  );

  const previewSection = (
    <section
      className="card space-y-4"
      aria-labelledby="signature-preview-title"
    >
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2
          id="signature-preview-title"
          className="text-base font-semibold text-gray-900"
        >
          {t('signature.preview.title')}
        </h2>
        <div className="tabs">
          {(['desktop', 'mobile'] as const).map((value) => (
            <button
              key={value}
              type="button"
              onClick={() => setViewport(value)}
              className={clsx(
                'tab',
                viewport === value ? 'tab-active' : 'tab-idle',
              )}
            >
              {t(
                value === 'desktop'
                  ? 'signature.preview.desktop'
                  : 'signature.preview.mobile',
              )}
            </button>
          ))}
        </div>
      </div>

      <div className="flex flex-wrap gap-x-5 gap-y-2">
        <CheckboxField
          label={t('signature.preview.blockImages')}
          checked={blockImages}
          onChange={setBlockImages}
        />
        <CheckboxField
          label={t('signature.preview.darkMode')}
          checked={darkMode}
          onChange={setDarkMode}
        />
      </div>
      {darkMode && (
        <p className="field-hint">{t('signature.preview.darkModeHint')}</p>
      )}

      <div className="rounded-lg border border-gray-200 bg-gray-100 p-3 overflow-x-auto">
        <PreviewFrame
          document={signaturePreviewDocument(html, {
            blockImages,
            darkMode,
          })}
          width={viewport === 'mobile' ? 375 : undefined}
          title={t('signature.preview.frameTitle')}
        />
      </div>

      {violations.length > 0 && (
        <StatusBanner tone="error">
          <p className="font-medium">{t('signature.lint.failed')}</p>
          <ul className="mt-1 list-disc pl-5 font-mono text-xs">
            {violations.map((violation, index) => (
              <li key={index}>
                {violation.rule}: {violation.excerpt}
              </li>
            ))}
          </ul>
        </StatusBanner>
      )}
      {!isValid && (
        <StatusBanner tone="warning">
          {t('signature.form.fixErrors')}
        </StatusBanner>
      )}

      <div className="flex flex-wrap gap-2">
        {copyButton('rich', 'signature.actions.copyRich', true)}
        {copyButton('text', 'signature.actions.copyText')}
        {copyButton('html', 'signature.actions.copyHtml')}
      </div>
    </section>
  );

  const installSection = (
    <section
      className="card space-y-4"
      aria-labelledby="signature-install-title"
    >
      <div>
        <h2
          id="signature-install-title"
          className="text-base font-semibold text-gray-900"
        >
          {t('signature.install.title')}
        </h2>
        <p className="mt-1 text-sm text-gray-600">
          {t('signature.install.lead')}
        </p>
      </div>

      <div className="tabs flex-wrap" role="tablist">
        {SIGNATURE_CLIENTS.map((value) => (
          <button
            key={value}
            type="button"
            role="tab"
            aria-selected={client === value}
            onClick={() => setClient(value)}
            className={clsx(
              'tab',
              client === value ? 'tab-active' : 'tab-idle',
            )}
          >
            {t(CLIENT_LABEL_KEYS[value])}
          </button>
        ))}
      </div>

      {client === 'appleMac' && (
        <>
          {device.macSafari && (
            <StatusBanner tone="warning">
              {t('signature.install.appleMac.safariWarning')}
            </StatusBanner>
          )}
          <Steps
            actions={copyButton('rich', 'signature.actions.copyRich', true)}
            steps={[
              tNode('signature.install.appleMac.step1', {
                button: strong(t('signature.actions.copyRich')),
              }),
              tNode('signature.install.appleMac.step2', {
                path: strong(t('signature.install.appleMac.settingsPath')),
                name: strong(fileBase),
              }),
              tNode('signature.install.appleMac.step3', {
                option: strong(t('signature.install.appleMac.matchFontOption')),
              }),
              t('signature.install.appleMac.step4'),
              t('signature.install.appleMac.step5'),
              t('signature.install.testMail'),
            ]}
            note={t('signature.install.appleMac.note')}
          />
          <details className="rounded-lg border border-gray-200 px-4 py-3">
            <summary className="cursor-pointer text-sm font-medium text-gray-900">
              {t('signature.install.appleMac.file.title')}
            </summary>
            <div className="mt-3">
              <Steps
                actions={copyButton('apple', 'signature.actions.copyApple')}
                steps={[
                  tNode('signature.install.appleMac.file.step1', {
                    name: strong(fileBase),
                    quit: strong(t('signature.install.appleMac.quitShortcut')),
                  }),
                  tNode('signature.install.appleMac.file.step2', {
                    path: code(APPLE_MAIL_PATH),
                    icloudPath: code(APPLE_MAIL_ICLOUD_PATH),
                  }),
                  tNode('signature.install.appleMac.file.step3', {
                    button: strong(t('signature.actions.copyApple')),
                  }),
                  t('signature.install.appleMac.file.step4'),
                  tNode('signature.install.appleMac.file.step5', {
                    option: strong(
                      t('signature.install.appleMac.matchFontOption'),
                    ),
                  }),
                  t('signature.install.testMail'),
                ]}
                note={t('signature.install.appleMac.file.note')}
              />
            </div>
          </details>
        </>
      )}
      {client === 'appleIos' && (
        <Steps
          actions={
            device.handheld
              ? copyButton('rich', 'signature.actions.copyRich', true)
              : phoneLink
          }
          steps={[
            ...(device.handheld ? [] : [t('signature.install.phone.openStep')]),
            tNode('signature.install.appleIos.step1', {
              button: strong(t('signature.actions.copyRich')),
            }),
            tNode('signature.install.appleIos.step2', {
              path: strong(t('signature.install.appleIos.settingsPath')),
            }),
            tNode('signature.install.appleIos.step3', {
              undo: strong(t('signature.install.appleIos.undoLabel')),
            }),
            t('signature.install.testMail'),
          ]}
          note={t('signature.install.appleIos.note')}
        />
      )}
      {client === 'thunderbird' && (
        <Steps
          actions={downloadButton('html')}
          steps={[
            t('signature.install.thunderbird.step1'),
            tNode('signature.install.thunderbird.step2', {
              option: strong(t('signature.install.thunderbird.fileOption')),
            }),
            tNode('signature.install.thunderbird.step3', {
              option: strong(t('signature.install.thunderbird.htmlOption')),
            }),
            tNode('signature.install.thunderbird.step4', {
              pref: code(THUNDERBIRD_SEPARATOR_PREF),
            }),
            t('signature.install.testMail'),
          ]}
          note={t('signature.install.thunderbird.note')}
        />
      )}
      {client === 'outlookClassic' && (
        <Steps
          actions={
            <>
              {downloadButton('htm')}
              {downloadButton('rtf')}
              {downloadButton('txt')}
            </>
          }
          steps={[
            t('signature.install.outlookClassic.step1'),
            tNode('signature.install.outlookClassic.step2', {
              path: code(OUTLOOK_PATH),
            }),
            tNode('signature.install.outlookClassic.step3', {
              name: strong(fileBase),
            }),
            t('signature.install.testMail'),
          ]}
          note={t('signature.install.outlookClassic.note')}
        />
      )}
      {client === 'outlookNew' && (
        <Steps
          actions={copyButton('rich', 'signature.actions.copyRich', true)}
          steps={[
            tNode('signature.install.outlookNew.step1', {
              button: strong(t('signature.actions.copyRich')),
            }),
            tNode('signature.install.outlookNew.step2', {
              path: strong(t('signature.install.outlookNew.settingsPath')),
            }),
            t('signature.install.outlookNew.step3'),
            t('signature.install.testMail'),
          ]}
          note={t('signature.install.outlookNew.note')}
        />
      )}
      {client === 'mobileApps' && (
        <Steps
          actions={
            device.handheld
              ? copyButton('text', 'signature.actions.copyText', true)
              : phoneLink
          }
          steps={[
            ...(device.handheld ? [] : [t('signature.install.phone.openStep')]),
            tNode('signature.install.mobileApps.step1', {
              button: strong(t('signature.actions.copyText')),
            }),
            tNode('signature.install.mobileApps.step2', {
              outlook: strong(t('signature.install.mobileApps.outlookPath')),
              gmail: strong(t('signature.install.mobileApps.gmailPath')),
            }),
          ]}
          note={t('signature.install.mobileApps.note')}
        />
      )}
    </section>
  );

  return (
    <>
      <PageHead
        eyebrow={t('signature.eyebrow')}
        title={t('signature.title')}
        lead={t('signature.lead')}
      />
      <PageBody>
        {notice && (
          <StatusBanner tone="success">
            {t(
              notice === 'saved'
                ? 'signature.library.savedNotice'
                : 'signature.library.deletedNotice',
            )}
          </StatusBanner>
        )}

        {device.handheld ? (
          // On the phone the page is an install screen first: the signature to pick, then the
          // guide with its copy button — not a long form to scroll past.
          <div className="space-y-5">
            {librarySection}
            {installSection}
            {previewSection}
            {formSection}
          </div>
        ) : (
          <div className="grid gap-5 xl:grid-cols-[minmax(0,26rem)_minmax(0,1fr)]">
            <div className="space-y-5 min-w-0">
              {librarySection}
              {formSection}
            </div>
            <div className="space-y-5 min-w-0">
              {previewSection}
              {installSection}
            </div>
          </div>
        )}

        <ConfirmDialog
          open={pending !== null}
          title={t('signature.library.discardTitle')}
          confirmLabel={t('signature.library.discardConfirm')}
          onConfirm={() => {
            if (pending) perform(pending);
            setPending(null);
          }}
          onCancel={() => setPending(null)}
        >
          <p>
            {tNode('signature.library.discardBody', {
              name: (
                <strong className="font-semibold text-gray-900">
                  {form.name}
                </strong>
              ),
            })}
          </p>
        </ConfirmDialog>

        <ConfirmDialog
          open={confirmingDelete && baseline !== null}
          title={t('signature.library.deleteTitle')}
          confirmLabel={t('signature.library.deleteConfirm')}
          confirmDisabled={remove.isPending}
          onConfirm={() => {
            if (baseline) remove.mutate(baseline.id);
          }}
          onCancel={() => setConfirmingDelete(false)}
        >
          <p>
            {tNode('signature.library.deleteBody', {
              name: (
                <strong className="font-semibold text-gray-900">
                  {baseline?.name ?? ''}
                </strong>
              ),
            })}
          </p>
        </ConfirmDialog>
      </PageBody>
    </>
  );
}

function TextField({
  id,
  label,
  value,
  onChange,
  onBlur,
  error,
  hint,
  placeholder,
  type = 'text',
  autoComplete,
}: {
  readonly id: string;
  readonly label: string;
  readonly value: string;
  readonly onChange: (value: string) => void;
  readonly onBlur: () => void;
  readonly error?: string;
  readonly hint?: string;
  readonly placeholder?: string;
  readonly type?: 'text' | 'email' | 'tel' | 'url';
  readonly autoComplete?: string;
}) {
  const describedBy = error ? `${id}-error` : hint ? `${id}-hint` : undefined;
  return (
    <div>
      <label htmlFor={id} className="field-label">
        {label}
      </label>
      <input
        id={id}
        type={type}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        onBlur={onBlur}
        placeholder={placeholder}
        autoComplete={autoComplete}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy}
        className="field-input"
      />
      {error ? (
        <p id={`${id}-error`} className="field-error">
          {error}
        </p>
      ) : (
        hint && (
          <p id={`${id}-hint`} className="field-hint">
            {hint}
          </p>
        )
      )}
    </div>
  );
}

function CheckboxField({
  label,
  hint,
  checked,
  onChange,
}: {
  readonly label: string;
  readonly hint?: string;
  readonly checked: boolean;
  readonly onChange: (checked: boolean) => void;
}) {
  return (
    <label className="flex items-start gap-2 text-sm text-gray-700">
      <input
        type="checkbox"
        checked={checked}
        onChange={(event) => onChange(event.target.checked)}
        className="mt-0.5 w-4 h-4 rounded border-gray-300"
      />
      <span>
        {label}
        {hint && <span className="block text-xs text-gray-500">{hint}</span>}
      </span>
    </label>
  );
}

function Steps({
  steps,
  actions,
  note,
}: {
  readonly steps: readonly ReactNode[];
  readonly actions: ReactNode;
  readonly note?: string;
}) {
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-2">{actions}</div>
      <ol className="list-decimal pl-5 space-y-2 text-sm text-gray-700">
        {steps.map((step, index) => (
          <li key={index}>{step}</li>
        ))}
      </ol>
      {note && <StatusBanner tone="info">{note}</StatusBanner>}
    </div>
  );
}

/**
 * The preview: the exact fragment, in a frame of its own so the dashboard's Tailwind cannot style
 * it — a signature that only looks right because the page around it supplies a font is precisely
 * the failure the preview must show.
 *
 * `allow-same-origin` WITHOUT `allow-scripts` lets the page read the frame's height to size it,
 * while nothing inside can run; the frame's own CSP additionally refuses everything but images.
 */
function PreviewFrame({
  document: source,
  width,
  title,
}: {
  readonly document: string;
  readonly width?: number;
  readonly title: string;
}) {
  const frame = useRef<HTMLIFrameElement>(null);
  const [height, setHeight] = useState(240);

  // The BODY, not `documentElement`: the root element's `scrollHeight` is floored at the frame's
  // own viewport height, so it could only ever grow — switching the legal block off would leave a
  // tall empty box behind. The body carries the frame's padding, so it measures the same box.
  function measure() {
    const body = frame.current?.contentDocument?.body;
    if (body) setHeight(Math.max(120, body.scrollHeight));
  }

  return (
    <iframe
      ref={frame}
      title={title}
      srcDoc={source}
      sandbox="allow-same-origin"
      onLoad={measure}
      className="block bg-white mx-auto rounded shadow-sm"
      style={{
        width: width ? `${width}px` : '100%',
        height: `${height}px`,
        border: 0,
      }}
    />
  );
}
